import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { isAdminUser } from '../../util/userHelpers';
import { approvePendingListing } from '../../util/api';
import { ADMIN_LISTING_API } from '../EditListingPage/listingApi';
import {
  clearPublishError,
  clearUpdatedTab,
  requestShowListing,
} from '../EditListingPage/EditListingPage.duck';
import { clearImage } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

// The admin listing editor runs on the expert editor's data layer: EditListingPage.duck
// (listing, images, availability exceptions) and ProfileSettingsPage.duck (the author's
// profile photo), both pointed at the admin endpoints with ADMIN_LISTING_API. Only the
// admin-specific pieces live here: approving a pending listing, and loading the page.

const { UUID } = sdkTypes;
const uuidString = id => (id && id.uuid ? id.uuid : id);

/////////////////////
// Approve Listing //
/////////////////////
// Reuses the onboarding approval endpoint (search rank, approval email, rating seed).
// It answers with a plain acknowledgement, so the listing is refetched afterwards.
export const approveListingThunk = createAsyncThunk(
  'AdminEditListingPage/approveListing',
  ({ listingId, config }, { dispatch, rejectWithValue }) => {
    return approvePendingListing(uuidString(listingId))
      .then(() => dispatch(requestShowListing({ id: listingId }, config, ADMIN_LISTING_API)))
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const requestApproveListing = (listingId, config) => dispatch => {
  return dispatch(approveListingThunk({ listingId, config })).unwrap();
};

// ================ Slice ================ //

const adminEditListingPageSlice = createSlice({
  name: 'AdminEditListingPage',
  initialState: {
    approveListingInProgress: false,
    approveListingError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(approveListingThunk.pending, state => {
        state.approveListingInProgress = true;
        state.approveListingError = null;
      })
      .addCase(approveListingThunk.fulfilled, state => {
        state.approveListingInProgress = false;
      })
      .addCase(approveListingThunk.rejected, (state, action) => {
        state.approveListingInProgress = false;
        state.approveListingError = action.payload;
      });
  },
});

export default adminEditListingPageSlice.reducer;

// ================ Load data ================ //

// Runs on the client for every tab of the editor (the route is auth-only, so the server
// never calls it). Availability exceptions are fetched by the panel's calendar itself.
export const loadData = (params, search, config) => (dispatch, getState) => {
  dispatch(clearUpdatedTab());
  dispatch(clearPublishError());
  // Never carry an uploaded-but-unsaved photo from one expert's form to another's.
  dispatch(clearImage());

  const currentUser = getState().user.currentUser;
  // Non-admins see the "access restricted" screen; don't hit the admin API for them.
  if (currentUser?.id && !isAdminUser(currentUser)) {
    return Promise.resolve();
  }
  return dispatch(requestShowListing({ id: new UUID(params.id) }, config, ADMIN_LISTING_API));
};
